import React, { FC, ReactNode } from 'react';
import { IProviderProps } from '../../store/payments/payments-interfaces-helper';
import { TablesNames } from '../../store/tables/tables-context';
import { formattedProviderData } from '../../store/tables/admin/provider-data';
import { Table } from '../../components/table/Table';
import { usePaymentContext } from '../../store/payments/payments-context';
import { useMerchants } from '../../store/merchnts/merchants-context';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IDropdownItems } from '../../components/disputes/disputes_queues/disputes_queue_details/DisputeQueuesTable';
import { faDisplay, faBan, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { IProviderFiltersProps } from '../../components/provider/ProviderFilters';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { IReleaseState } from '../../routing/routing-helper/provider-navigation';
import { MenuItem } from '@mui/material';

enum PROVIDER_ACTION_NAMES {
  VIEW = 'View',
  RELEASE = 'Release',
  DEACTIVATE = 'Deactivate',
}

interface Props {
  listener?: (search: any) => Promise<void>;
  quarantinedProvidersList?: IProviderProps[];
  setReleaseState?: (state: IReleaseState) => void;
  setDeactivateModalState?: (state: IReleaseState) => void;
}

export const ProvidersQuarantine: FC<Props> = (props) => {
  const { listener, quarantinedProvidersList, setReleaseState, setDeactivateModalState } = props;

  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const { merchantState: { isListOfMerchantsLoading, isPartnerNamesListLoading }} = useMerchants();
  const { paymentContextState } = usePaymentContext();
  const { isProviderDataLoading } = paymentContextState;

  const filtersFromUrl: IProviderFiltersProps = parseSearchParams(search) || {};

  const openProviderDetails = (providerId: string): void => {
    const params: IProviderFiltersProps = { ...filtersFromUrl };
    params.providerId = providerId;

    navigate(`${pathname}?${makeSearchParams(params)}`);
  };

  const dropDownItems = [
    {
      name: PROVIDER_ACTION_NAMES.VIEW,
      picture: faDisplay,
      action: (id: string): void => openProviderDetails(id),
    },
    {
      name: PROVIDER_ACTION_NAMES.RELEASE,
      picture: faPaperPlane,
      action: (id: string): void => setReleaseState({ isOpen: true, providerId: id }),
    },
    {
      name: PROVIDER_ACTION_NAMES.DEACTIVATE,
      picture: faBan,
      action: (id: string): void => setDeactivateModalState({ isOpen: true, providerId: id }),
    },
  ];

  const rowActionMenuItems = (rowData, closeMenu): ReactNode[] => {
    const filteredDropDownItems = dropDownItems.filter((item) =>
      item?.name === PROVIDER_ACTION_NAMES.VIEW ||
      rowData?.original?.cycleMetrics?.quarantine_AddedOn && item?.name === PROVIDER_ACTION_NAMES.RELEASE ||
      rowData?.original?.isActive && item?.name === PROVIDER_ACTION_NAMES.DEACTIVATE
    );

    return filteredDropDownItems?.map(({ name, picture, action }: IDropdownItems) => {
      return (
        <MenuItem
          key={name}
          onClick={(): void => {
            action(rowData?.original?.id);
            closeMenu();
          }}
          className={clsx('fw-bolder fs-7 main-font-family', name === PROVIDER_ACTION_NAMES.DEACTIVATE ? 'text-danger' : 'text-muted')}
        >
          <FontAwesomeIcon icon={picture} className="me-1"/>
          {name}
        </MenuItem>
      );
    });
  };

  return (
    <div>
      <Table
        name={TablesNames.quarantinedProvidersTableData}
        data={formattedProviderData(quarantinedProvidersList) ?? []}
        isLoading={isProviderDataLoading || isListOfMerchantsLoading || isPartnerNamesListLoading}
        queryParamsListener={listener}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableDensityToggle={false}
        enableColumnActions={false}
        enableTopToolbar={false}
        enableRowActions={true}
        rowActionMenuItems={rowActionMenuItems}
        actionColumnAlign="left"
        actionColumnsSize={10}
      />
    </div>
  );
};